/**
 * 型定義
 */

import type { AgeGroup, FilterResult, BlockCategory } from '../constants';
import type { Language } from '../i18n/config';

// Re-export constants
export { AgeGroup } from '../constants';
export type { FilterResult, BlockCategory };

// ==================== API型定義 ====================

export interface SessionInfo {
  session_id: string;
  user_id: string;
  age_group: AgeGroup;
  start_time: number;
  last_activity: number;
  message_count: number;
  title?: string;
  last_message?: string;
  ttl?: number;
  deleted_at?: number;
}

export interface AuthUser {
  age_group: AgeGroup;
  recommended_language: Language;
}

export interface ChatHistoryResponse {
  sessions: SessionInfo[];
  user: AuthUser | null;
  unauthenticated: boolean;
  paidPlanRequired: boolean;
}

export interface FilteringResult {
  result: FilterResult;
  confidence: number;
  layer: number;
  block_category?: BlockCategory;
  reason?: string;
  details?: Record<string, unknown>;
}

export interface BaseChatMessage {
  id: string;
  role?: 'user' | 'assistant';
  content?: string;
  timestamp: number;
  filtered?: boolean;
  session_id: string;
  user_input: string;
  ai_response: string;
  age_group: AgeGroup;
}

export interface ChatRequest {
  message: string;
  session_id: string;
  age_group: AgeGroup;
  user_id?: string;
  conversation_history?: Array<{ role: string; content: string }>;
  attached_file_ids?: string[];
  language?: string;
}

export interface GeneratedImageInfo {
  image_id: string;
  image_url: string;
  prompt: string;
  confidence: number;
}

export interface SourceInfo {
  url: string;
  title?: string;
}

export type ChatEventType =
  | 'rate_limit_hourly'
  | 'rate_limit_monthly'
  | 'content_filter_input'
  | 'content_filter_output'
  | 'guardrails'
  | 'generation_error';

export interface ChatEventParams {
  limit?: number | null;
  reset_at?: number | null;
  block_category?: BlockCategory | null;
}

export interface ChatEvent {
  type: ChatEventType;
  params: ChatEventParams;
}

export interface ChatLimitInfo {
  remaining: number | null;
  limit: number | null;
  reset_at: number | null;
}

export interface UsageLimits {
  chat: ChatLimitInfo;
}

export interface ChatResponse {
  message?: string;
  message_id: string;
  filtered: boolean;
  filtering_result?: FilteringResult;
  event?: ChatEvent;
  chat_limit?: ChatLimitInfo;
  generated_image?: GeneratedImageInfo;
  sources?: SourceInfo[];
}

export interface ImageGenerationRequest {
  prompt: string;
  session_id?: string;
  age_group: AgeGroup;
  user_id?: string;
  negative_prompt?: string;
  width?: number;
  height?: number;
  cfg_scale?: number;
  steps?: number;
  attached_file_ids?: string[];
}

export interface ImageGenerationResponse {
  image_id: string;
  filtered: boolean;
  status: string;
  image_url?: string;
  image_base64?: string;
  filtering_result?: FilteringResult;
  error_message?: string;
}

export interface ContentFilterRequest {
  text: string;
  age_group: AgeGroup;
  session_id: string;
  message_type: 'user_input' | 'ai_output';
}

export interface ContentFilterResponse {
  allowed: boolean;
  filtering_result: FilteringResult;
  sanitized_text?: string;
}

// ==================== API応答の型定義 ====================

/**
 * バックエンドからの添付ファイル情報（snake_case）
 */
export interface AttachedFileInfoFromBackend {
  file_id: string;
  filename: string;
  mime_type: string;
}

/**
 * チャットログのAPI応答型
 * バックエンドの chat_logger.py に対応
 */
export interface ChatMessageRecord {
  id: string;
  timestamp: number;
  session_id: string;
  user_input: string;
  ai_response: string;
  filtering_result?: FilteringResult;
  block_category?: string;
  event_type?: ChatEventType;
  age_group: string;
  ttl?: number;
  attached_files?: AttachedFileInfoFromBackend[];
  sources?: SourceInfo[];
}

/**
 * 画像ログのAPI応答型
 * バックエンドの image_logger.py に対応
 */
export interface ImageRecord {
  id: string;
  timestamp: number;
  session_id: string;
  prompt: string;
  image_id: string;
  image_url?: string;
  image_base64?: string;
  age_group: string;
  filtered: boolean;
  error?: string;
  ttl?: number;
  deleted_at?: number;
}

// ==================== UI共通型定義 ====================

/**
 * 表示モード（チャット/画像生成）
 */
export const ViewMode = {
  CHAT: 'chat',
  IMAGE: 'image',
} as const;

export type ViewMode = (typeof ViewMode)[keyof typeof ViewMode];

/**
 * セッション初期化の状態
 */
export const SessionInitStatus = {
  LOADING: 'loading',
  READY: 'ready',
  UNAUTHENTICATED: 'unauthenticated',
  PAID_PLAN_REQUIRED: 'paidPlanRequired',
  ERROR: 'error',
} as const;

export type SessionInitStatus = (typeof SessionInitStatus)[keyof typeof SessionInitStatus];

/**
 * アイコンコンポーネント共通Props
 */
export interface IconProps {
  width?: number;
  height?: number;
}

// ==================== フロントエンド固有の型定義 ====================

export interface AttachedFileInfo {
  file_id: string;
  filename: string;
  mimeType: string;
}

export interface ChatMessage extends BaseChatMessage {
  isUser: boolean;
  isLoading?: boolean;
  isError?: boolean;
  isStreaming?: boolean;
  attachedFiles?: AttachedFileInfo[];
  generatedImage?: GeneratedImageInfo;
  sources?: SourceInfo[];
  event?: ChatEvent;
}

export interface AppState {
  sessionId: string | null;
  userId: string | null;
  ageGroup: AgeGroup | null;
  messages: ChatMessage[];
  isConnected: boolean;
}

export interface ChatSession {
  id: string;
  title: string;
  timestamp: number;
  ageGroup: AgeGroup;
  messageCount: number;
  lastMessage?: string;
}

// ==================== ファイルアップロード関連の型定義 ====================

export interface FileAttachment {
  data: string; // Base64 encoded file data
  filename: string;
  mimeType: string;
}

export interface UploadedFile {
  success: boolean;
  file_id?: string;
  filename?: string;
  mime_type?: string;
  file_size?: number;
  s3_key?: string;
  error?: string;
}

export interface FileUploadRequest {
  session_id: string;
  files: FileAttachment[];
}

export interface FileUploadResponse {
  results: UploadedFile[];
}

// ==================== Blocklyフィードバック型定義 ====================

export interface BlocklyProgramAnalysis {
  total_blocks: number;
  block_types: Record<string, number>;
  nested_depth: number;
  has_loops: boolean;
  has_conditions: boolean;
  has_variables: boolean;
  has_functions: boolean;
  has_events: boolean;
  code_structure: string[];
}

export interface BlocklyFeedbackRequest {
  project_data: string;
  data_format: 'xml' | 'json';
  age_group: AgeGroup;
  session_id: string;
  user_id?: string;
  learning_summary?: string;
  language?: 'ja' | 'en';
}

export interface BlocklyFeedbackResponse {
  feedback_id: string;
  success: boolean;
  analysis?: BlocklyProgramAnalysis;
  feedback?: string;
  strengths?: string[];
  improvements?: string[];
  next_steps?: string[];
  error_message?: string;
}
